'use client'

import { useState, useEffect, useMemo } from 'react'
import { useUser } from '@/lib/context/user-context'
import { useUserSettings } from '@/contexts/user-settings-context'

interface PodcastInfo {
  id: string
  title: string
  author?: string
  description?: string
  imageUrl?: string
  feedUrl?: string
  totalEpisodes?: number
  lastEpisodeDate?: string
  categories?: string[]
  defaultLevelRange?: string[] | string | null
}

export function useSubscribedPodcasts() {
  const { user } = useUser()
  const { settings, refreshUserSettings, hasLoaded: hasLoadedSettings } = useUserSettings()
  const [subscribedPodcasts, setSubscribedPodcasts] = useState<PodcastInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Get subscribed podcast IDs from user settings context
  const subscribedPodcastIds = useMemo(
    () => settings?.subscribedPodcastIds ?? [],
    [settings?.subscribedPodcastIds]
  )
  const subscribedPodcastsSnapshot = useMemo(
    () => settings?.subscribedPodcastsSnapshot ?? {},
    [settings?.subscribedPodcastsSnapshot]
  )

  const isSnapshotStale = (updatedAt?: string) => {
    if (!updatedAt) return true
    const updated = Date.parse(updatedAt)
    if (Number.isNaN(updated)) return true
    const ONE_DAY_MS = 24 * 60 * 60 * 1000
    return Date.now() - updated > ONE_DAY_MS
  }

  // Fetch podcast details for each subscribed podcast
  useEffect(() => {
    const fetchPodcastDetails = async () => {
      // If settings haven't loaded yet, try to use snapshot data if available
      if (!hasLoadedSettings) {
        // Check if we have any snapshot data available
        const snapshotPodcasts = subscribedPodcastIds
          .map((id) => subscribedPodcastsSnapshot[id])
          .filter(Boolean)
          .map((snap: any) => ({
            id: snap.id,
            title: snap.title,
            author: snap.author,
            imageUrl: snap.imageUrl,
            feedUrl: snap.feedUrl,
            totalEpisodes: snap.totalEpisodes,
            defaultLevelRange: snap.defaultLevelRange,
            categories: snap.categories
          })) as PodcastInfo[]

        if (snapshotPodcasts.length > 0) {
          // Use snapshot data immediately for faster render
          setSubscribedPodcasts(snapshotPodcasts)
          setIsLoading(false)
        } else if (subscribedPodcastIds.length === 0) {
          // No subscriptions, show empty state
          setSubscribedPodcasts([])
          setIsLoading(false)
        }
        // Otherwise, keep loading state until settings load
        return
      }

      if (subscribedPodcastIds.length === 0) {
        setSubscribedPodcasts([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Use snapshot first for fast render
        const snapshotPodcasts = subscribedPodcastIds
          .map((id) => subscribedPodcastsSnapshot[id])
          .filter(Boolean)
          .map((snap: any) => ({
            id: snap.id,
            title: snap.title,
            author: snap.author,
            imageUrl: snap.imageUrl,
            feedUrl: snap.feedUrl,
            totalEpisodes: snap.totalEpisodes,
            defaultLevelRange: snap.defaultLevelRange,
            categories: snap.categories
          })) as PodcastInfo[]

        if (snapshotPodcasts.length > 0) {
          setSubscribedPodcasts(snapshotPodcasts)
          // Don't set isLoading to false here - wait for fresh data if needed
        }

        const missingOrStaleIds = subscribedPodcastIds.filter((id) => {
          const snap = subscribedPodcastsSnapshot[id]
          return !snap || isSnapshotStale(snap.updatedAt) || !snap.title
        })

        if (missingOrStaleIds.length === 0) {
          setIsLoading(false)
          return
        }

        const podcastsData = await Promise.all(
          missingOrStaleIds.map(async (podcastId) => {
            try {
              // Handle YouTube Channels (ID starts with UC)
              if (podcastId.startsWith('UC')) {
                const response = await fetch(`/api/channel/${encodeURIComponent(podcastId)}`)
                if (response.ok) {
                  const data = await response.json()
                  return {
                    id: podcastId,
                    title: data.name,
                    author: data.name,
                    description: data.description,
                    imageUrl: data.avatar,
                    totalEpisodes: data.episodes?.length || 0,
                    lastEpisodeDate: data.episodes?.[0]?.publishDate,
                    categories: data.topics || []
                  }
                }
                return null
              }

              // Handle Standard Podcasts
              const response = await fetch(`/api/podcast/${podcastId}`)
              if (response.ok) {
                const data = await response.json()
                return {
                  id: podcastId,
                  title: data.title,
                  author: data.author,
                  description: data.description,
                  imageUrl: data.imageUrl,
                  totalEpisodes: data.totalEpisodes,
                  lastEpisodeDate: data.episodes?.[0]?.publishDate,
                  categories: data.categories
                }
              }
              return null
            } catch (error) {
              console.error('Error fetching podcast:', podcastId, error)
              return null
            }
          })
        )

        const validPodcasts = podcastsData.filter((p) => p !== null) as PodcastInfo[]
        // Merge snapshot + fresh fetch
        const mergedMap = new Map<string, PodcastInfo>()
        snapshotPodcasts.forEach((p) => mergedMap.set(p.id, p))
        validPodcasts.forEach((p) => mergedMap.set(p.id, p))
        setSubscribedPodcasts(Array.from(mergedMap.values()))
      } catch (err) {
        console.error('Error fetching podcast details:', err)
        setError('Failed to load podcast details')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPodcastDetails()
  }, [subscribedPodcastIds, subscribedPodcastsSnapshot, hasLoadedSettings])

  const refetchSubscriptions = async () => {
    // Trigger a refresh of user settings to get updated subscription data
    setIsLoading(true)
    await refreshUserSettings()
    // The useEffect will automatically re-run when subscribedPodcastIds changes
  }

  return {
    subscribedPodcastIds,
    subscribedPodcasts,
    isLoading,
    error,
    refetchSubscriptions
  }
}
